var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from 'app/core/api/api.js';
import { updateDataFromCollection } from 'app/store/reducers/utils';
import { updateTasksStatus, updateTask } from 'app/store/reducers/tasks';
import { createNewTakeIfStatusIsRetake } from 'app/store/hooks/takes.js';
const initialState = {
    data: [],
    loading: false,
    count: 0,
};
const removeDuplicates = (data, key) => {
    return [...new Map(data.map((x) => [key(x), x])).values()];
};
export const createTake = createAsyncThunk('takes/create', (takes_1, _a) => __awaiter(void 0, [takes_1, _a], void 0, function* (takes, { fulfillWithValue, rejectWithValue, dispatch }) {
    try {
        if (Array.isArray(takes)) {
            const response = yield api.takes.collection.create(takes);
            return fulfillWithValue(response);
        }
        const response = yield api.takes.create(takes);
        return fulfillWithValue(response);
    }
    catch (error) {
        return rejectWithValue(error);
    }
}));
export const updateTake = createAsyncThunk('takes/update', (updatedTake_1, _a) => __awaiter(void 0, [updatedTake_1, _a], void 0, function* (updatedTake, { fulfillWithValue, rejectWithValue, dispatch, getState, }) {
    try {
        if (Array.isArray(updatedTake)) {
            const takes = getState().reduxTakes.data;
            const relatedTasks = getState().reduxTasks.relatedTasks.data;
            const taskWithRelatedTakes = relatedTasks.map((task) => {
                const relatedTakes = takes.filter((take) => take.task === task.id);
                return Object.assign(Object.assign({}, task), { takesInst: relatedTakes });
            });
            yield createNewTakeIfStatusIsRetake(updatedTake, takes, taskWithRelatedTakes);
            const response = yield api.takes.collection.update(updatedTake);
            dispatch(updateTasksStatus([...response.map((take) => ({ id: take.task, status: take.status }))]));
            return fulfillWithValue(response);
        }
        else {
            const response = yield api.takes.update(updatedTake);
            dispatch(updateTasksStatus({ id: response.task, status: response.status }));
            return fulfillWithValue(response);
        }
    }
    catch (error) {
        return rejectWithValue(error);
    }
}));
export const takePushTo = createAsyncThunk('takes/pushTo', (value_1, _a) => __awaiter(void 0, [value_1, _a], void 0, function* (value, { fulfillWithValue, rejectWithValue }) {
    try {
        const response = yield api.pushTo(value);
        return fulfillWithValue(response.results);
    }
    catch (error) {
        return rejectWithValue(error);
    }
}));
export const takePushToFromRetake = createAsyncThunk('takes/pushToFromRetake', (value_1, _a) => __awaiter(void 0, [value_1, _a], void 0, function* (value, { fulfillWithValue, rejectWithValue, dispatch }) {
    try {
        const response = yield api.pushTo(value);
        dispatch(updateTask({
            updatedTask: response.results.map((lastTake) => ({
                id: lastTake.task,
                status: lastTake.status,
            })),
        }));
        return fulfillWithValue(response.results);
    }
    catch (error) {
        return rejectWithValue(error);
    }
}));
export const updateTakeAction = updateTake.fulfilled;
const { actions, reducer } = createSlice({
    name: 'reduxTakes',
    initialState,
    reducers: {
        setAllTakes: (state, { payload }) => {
            const newData = [...state.data, ...payload];
            const parsedNewData = removeDuplicates(newData, (take) => take.id);
            state.data = parsedNewData;
        },
        createTakeFromWS: (state, { payload }) => {
            const updatedPayload = Object.assign(Object.assign({}, payload), { id: payload.uuid });
            delete updatedPayload.uuid;
            const t = [...state.data, updatedPayload];
            const newData = removeDuplicates(t, (note) => note.id);
            state.data = newData;
        },
        updateTakeFromWS: (state, { payload }) => {
            const updatedPayload = Object.assign(Object.assign({}, payload), { id: payload.uuid });
            delete updatedPayload.uuid;
            const newData = state.data.map((take) => {
                if (take.id === updatedPayload.id) {
                    return Object.assign(Object.assign({}, take), payload);
                }
                return take;
            });
            state.data = newData;
        },
    },
    extraReducers: (builder) => {
        // CREATE TAKE
        builder.addCase(createTake.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(createTake.fulfilled, (state, { payload }) => {
            let newData;
            if (Array.isArray(payload)) {
                newData = [...state.data, ...payload];
            }
            else {
                newData = [...state.data, payload];
            }
            state.data = newData;
            state.loading = false;
        });
        builder.addCase(createTake.rejected, (state) => {
            state.loading = false;
        });
        // UPDATE TAKE
        builder.addCase(updateTake.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(updateTakeAction, (state, { payload }) => {
            let newData;
            if (Array.isArray(payload)) {
                newData = updateDataFromCollection(state.data, payload);
            }
            else {
                newData = state.data.map((take) => {
                    if (take.id === payload.id) {
                        return payload;
                    }
                    return take;
                });
            }
            state.data = newData;
            state.loading = false;
        });
        builder.addCase(updateTake.rejected, (state) => {
            state.loading = false;
        });
        // PUSH TO
        builder.addCase(takePushTo.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(takePushTo.fulfilled, (state, { payload }) => {
            const newData = updateDataFromCollection(state.data, payload);
            state.data = newData;
            state.loading = false;
        });
        builder.addCase(takePushTo.rejected, (state) => {
            state.loading = false;
        });
        // PUSH TO FROM RETAKE
        builder.addCase(takePushToFromRetake.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(takePushToFromRetake.fulfilled, (state, { payload }) => {
            const newData = [...state.data, ...payload];
            state.data = newData;
            state.loading = false;
        });
        builder.addCase(takePushToFromRetake.rejected, (state) => {
            state.loading = false;
        });
    },
});
export const { setAllTakes, createTakeFromWS, updateTakeFromWS } = actions;
export default reducer;
